import React, { useEffect, useState } from "react";
import { Layout, Breadcrumb, theme } from "antd";
import {
  useNavigate,
  useLocation,
  useNavigate as useNav,
} from "react-router-dom";
import "./MainLayout.css";
import "./ListLayout.css";
import PageRoute from "./PageRoute";
import AppSidebar from "./AppSidebar";
import AppHeader from "./AppHeader";
import UnAuthRoute from "./UnAuthRoute";
import { useUser } from "../Context/UserContext";
import {
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  BREAKPOINT_SIDEBAR,
} from "./Layout/constants";

const { Content } = Layout;

const MainLayout: React.FC = () => {
  const { token } = theme.useToken();
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [themeMode, setThemeMode] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme") as "light" | "dark") || "light",
  );

  useEffect(() => {
    const handleThemeChange = (e: Event) => {
      setThemeMode((e as CustomEvent).detail as "light" | "dark");
    };
    window.addEventListener("themeChange", handleThemeChange);
    return () => window.removeEventListener("themeChange", handleThemeChange);
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      const isPublicPath =
        location.pathname.startsWith("/login") ||
        location.pathname.startsWith("/forgot-password") ||
        location.pathname.startsWith("/reset-password") ||
        location.pathname.startsWith("/data-request");

      if (!isPublicPath) {
        navigate("/login");
      }
    }
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (width >= 1400) setCollapsed(false);
    else if (width >= BREAKPOINT_SIDEBAR) setCollapsed(true);
  }, [width]);

  const onCollapse = () => {
    if (width >= BREAKPOINT_SIDEBAR) setCollapsed((x) => !x);
  };

  const getLayoutStyle = (): React.CSSProperties => {
    const bg =
      themeMode === "dark" ? token.colorBgLayout : token.colorFillAlter;
    if (width < BREAKPOINT_SIDEBAR)
      return {
        background: bg,
        minHeight: "100vh",
        width: "100%",
        marginLeft: 0,
      };
    if (collapsed)
      return {
        background: bg,
        minHeight: "100vh",
        width: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
        marginLeft: SIDEBAR_COLLAPSED_WIDTH,
      };
    return {
      background: bg,
      minHeight: "100vh",
      width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
      marginLeft: SIDEBAR_WIDTH,
    };
  };

  return (
    <>
      {isAuthenticated() ? (
        <Layout
          hasSider
          style={
            {
              background:
                themeMode === "dark"
                  ? token.colorBgLayout
                  : token.colorFillAlter,
              minHeight: "100vh",
              "--primary-color": token.colorPrimary,
            } as React.CSSProperties
          }
        >
          <AppSidebar
            collapsed={collapsed}
            onCollapse={onCollapse}
            sidebarOpen={sidebarOpen}
            onSidebarClose={() => setSidebarOpen(false)}
            screenWidth={width}
          />

          <Layout className="site-layout" style={getLayoutStyle()}>
            <AppHeader
              screenWidth={width}
              onMenuOpen={() => setSidebarOpen(true)}
            />
            <Content
              className={themeMode === "dark" ? "" : "site-layout-background"}
              style={{ margin: "12px 10px" }}
            >
              <PageRoute />
            </Content>
          </Layout>
        </Layout>
      ) : (
        <Layout className="site-layout">
          <Content>
            <UnAuthRoute />
          </Content>
        </Layout>
      )}
    </>
  );
};

export default MainLayout;

// ─── Legacy PageHeader export ─────────────────────────────────────────────────
// Kept for backward compatibility. New pages should use Layout/PageHeader instead.

interface BreadCrumbItem {
  label: string | React.ReactNode;
  path?: string;
  style?: React.CSSProperties;
}

interface BreadcrumbItemProps {
  item: BreadCrumbItem[];
  separator?: React.ReactNode;
  style?: React.CSSProperties;
}

export const PageHeader = ({ item, separator, style }: BreadcrumbItemProps) => {
  const nav = useNav();
  return (
    <Breadcrumb
      style={style ?? { color: "#1677FF" }}
      separator={separator ?? ">"}
    >
      {item?.map((crumb, index) => (
        <Breadcrumb.Item key={index}>
          {crumb?.path ? (
            <span
              style={crumb?.style ?? { color: "#0093FF" }}
              onClick={() => {
                if (crumb?.path) nav(crumb.path);
              }}
            >
              {crumb?.label}
            </span>
          ) : (
            crumb?.label
          )}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
};
